/**
 * Guardar un Blob eligiendo ubicación (File System Access API) o descarga clásica.
 *
 * Importante: llamar `prepareBlobSaveTarget` en el mismo gesto del usuario (click),
 * ANTES de esperar la respuesta del API. Si se pide el diálogo al final de una
 * descarga larga, el navegador puede rechazar showSaveFilePicker por falta de
 * activación de usuario.
 *
 * Tras una espera larga (o en webviews / preview del IDE), createWritable puede
 * fallar con NotAllowedError; en ese caso se cae a <a download> sin romper el flujo.
 */

import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";

/**
 * @param {{ filename: string, mimeType?: string, extensions?: string[], description?: string }} opts
 * @returns {Promise<{ mode: 'picker'|'download', handle?: FileSystemFileHandle, filename: string }>}
 */
export async function prepareBlobSaveTarget({
  filename,
  mimeType = "application/octet-stream",
  extensions = [],
  description = "Archivo",
}) {
  const safeName = String(filename || "archivo").replace(/[/\\?%*:|"<>]/g, "_");
  // Preview IDE / iframe: showSaveFilePicker a veces abre, pero createWritable falla después.
  if (typeof window !== "undefined"
    && typeof window.showSaveFilePicker === "function"
    && !isEmbeddedBrowserContext()) {
    try {
      const types =
        extensions.length > 0
          ? [{ description, accept: { [mimeType]: extensions } }]
          : undefined;
      const handle = await window.showSaveFilePicker({
        suggestedName: safeName,
        ...(types ? { types } : {}),
      });
      return { mode: "picker", handle, filename: safeName };
    } catch (e) {
      if (e?.name === "AbortError") {
        const err = new Error("canceled");
        err.name = "AbortError";
        err.canceled = true;
        throw err;
      }
      // Permiso denegado / API incompleta → descarga clásica.
    }
  }
  return { mode: "download", filename: safeName };
}

function downloadViaAnchor(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 300000);
  return { mode: "download" };
}

/**
 * Escribe el blob en el destino elegido o dispara <a download>.
 * Si createWritable falla (contexto no permitido tras espera larga), usa descarga clásica.
 * @param {{ mode: string, handle?: FileSystemFileHandle, filename?: string }|null|undefined} target
 * @param {Blob} blob
 * @param {string} [fallbackName]
 * @returns {Promise<{ mode: 'picker'|'download', fellBack?: boolean }>}
 */
function withWriteTimeout(promise, ms = 12000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error("timeout");
      err.name = "TimeoutError";
      reject(err);
    }, ms);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export async function commitBlobSave(target, blob, fallbackName) {
  const name = String(fallbackName || target?.filename || "archivo").replace(/[/\\?%*:|"<>]/g, "_");
  if (target?.mode === "picker" && target.handle && typeof target.handle.createWritable === "function") {
    try {
      const writable = await withWriteTimeout(target.handle.createWritable());
      try {
        await withWriteTimeout(writable.write(blob));
      } finally {
        try {
          await writable.close();
        } catch {
          /* ignore close errors after write failure */
        }
      }
      return { mode: "picker" };
    } catch (e) {
      // NotAllowedError / SecurityError / "not allowed by the user agent…"
      // Tras generate PDF/Excel largo o en webview: el handle ya no es escribible.
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[saveBlobAs] createWritable no permitido; descarga clásica", e?.name || e);
      }
      return { ...downloadViaAnchor(blob, name), fellBack: true };
    }
  }
  return downloadViaAnchor(blob, name);
}
